import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { container } from "tsyringe";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import { useNavigateToHomePage } from "hooks/useNavigateToHomePage";
import ProjectService from "services/ProjectService";

const projectService = container.resolve(ProjectService);

export type IProjectContext = {
	project: ProjectResponseResource;
};

type IProps = {
	children: ReactNode;
};

const ProjectContext = createContext<IProjectContext>(undefined!);

export function ProjectProvider(props: IProps) {
	const { project } = useProject();

	return (
		project && (
			<ProjectContext.Provider
				value={{
					project,
				}}>
				{props.children}
			</ProjectContext.Provider>
		)
	);
}

export const useProjectContext = () => {
	const context = useContext(ProjectContext);

	if (context === undefined) {
		throw new Error("useProjectContext must be used within a ProjectProvider");
	}

	return context;
};

const useProject = () => {
	const params = useParams();
	const { navigateToHomePage } = useNavigateToHomePage();
	const [project, setProject] = useState<ProjectResponseResource | null>(null);

	const fetchProject = useCallback(async () => {
		const projectId = params["projectId"];

		if (!projectId) {
			console.error("Project id not found in url");
			navigateToHomePage();
			return;
		}

		projectService
			.getProjectById(projectId)
			.then(setProject)
			.catch(() => {
				console.error("Failed to fetch project");
				navigateToHomePage();
			});
	}, [navigateToHomePage, params]);

	useEffect(() => {
		fetchProject();
	}, [fetchProject]);

	return {
		project,
	};
};
